/**
 * Admin — Security/Threat Response (Wave 1b W1b-14 + W1b-15 + W1b-16)
 *
 * Three sections wired to /api/v1/admin/security/threat/*:
 *   1. Anomaly rules    — full CRUD + toggle, structured threshold editor
 *   2. Security incidents — filterable list + state-machine transitions
 *   3. Block lists      — CRUD + bulk-unblock by source
 *
 * Mock data has been removed; first paint pulls from the backend (incidents
 * are seeded for the F61 admin org by the b39 migration so the page is
 * never empty in dev).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldAlert, Ban, Target, Eye, Pencil, Plus, Trash2,
  CheckCircle2, XCircle, Activity, Clock,
  Search, ChevronDown, AlertOctagon, Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { SectionCard, StatusDot } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';

// ─── Backend types ────────────────────────────────────────────────────────

type RuleType =
  | 'impossible_travel'
  | 'brute_force'
  | 'credential_stuffing'
  | 'privilege_escalation'
  | 'data_exfiltration'
  | 'token_reuse'
  | 'api_abuse'
  | 'session_hijack';

type Severity = 'low' | 'medium' | 'high' | 'critical';

type RuleAction =
  | 'alert_only'
  | 'require_mfa'
  | 'block_session'
  | 'disable_account';

type IncidentStatus =
  | 'open'
  | 'investigating'
  | 'resolved'
  | 'false_positive';

type BlockListType =
  | 'ip'
  | 'subnet'
  | 'user_agent'
  | 'api_key'
  | 'user_account'
  | 'email_domain'
  | 'country'
  | 'asn';

type BlockListSource = 'manual' | 'anomaly_rule' | 'threat_intel_feed';

interface AnomalyRule {
  id: string;
  name: string;
  description: string | null;
  ruleType: RuleType;
  severity: Severity;
  action: RuleAction;
  threshold: Record<string, unknown>;
  isEnabled: boolean;
  matchCount24h: number;
  lastTriggeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface IncidentSummary {
  id: string;
  ruleId: string | null;
  ruleType: string | null;
  affectedUserId: string | null;
  affectedIp: string | null;
  severity: Severity;
  status: IncidentStatus;
  title: string;
  summary: string | null;
  actionTaken: string | null;
  triggeredAt: string;
  resolvedAt: string | null;
}

interface IncidentDetail extends IncidentSummary {
  evidence: Record<string, unknown>;
  assignedTo: string | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BlockListEntry {
  id: string;
  listType: BlockListType;
  value: string;
  source: BlockListSource;
  reason: string | null;
  hitCount: number;
  expiresAt: string | null;
  createdAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  impossible_travel: 'Impossible Travel',
  brute_force: 'Brute Force',
  credential_stuffing: 'Credential Stuffing',
  privilege_escalation: 'Privilege Escalation',
  data_exfiltration: 'Data Exfiltration',
  token_reuse: 'Token Reuse',
  api_abuse: 'API Abuse',
  session_hijack: 'Session Hijack',
};

// Threshold defaults per rule type (mirrors ADMIN-FUNCTIONAL-SPEC §4.5).
const DEFAULT_THRESHOLDS: Record<RuleType, Record<string, unknown>> = {
  impossible_travel: { minKm: 500, maxMinutes: 60 },
  brute_force: { failuresPerMinute: 10, windowMinutes: 5 },
  credential_stuffing: { failuresPerMinute: 50, uniqueAccounts: 20 },
  privilege_escalation: { baselineDays: 30 },
  data_exfiltration: { multiplier: 10, baselineDays: 30 },
  token_reuse: {},
  api_abuse: { violationsPerHour: 5 },
  session_hijack: { enforceUa: true, enforceIp: true },
};

export const Route = createFileRoute('/admin/security/threat')({
  component: ThreatPage,
});

function ThreatPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Threat Response</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Anomaly detection rules, recent incidents, and block lists.
        </p>
      </div>

      <AnomalyRulesSection />
      <IncidentsSection />
      <BlockListSection />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Anomaly Rules (W1b-14)
// ═══════════════════════════════════════════════════════════════════════════

function AnomalyRulesSection() {
  const [rules, setRules] = useState<AnomalyRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AnomalyRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AnomalyRule | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ rules: AnomalyRule[] }>(
        '/admin/security/threat/rules'
      );
      setRules(data.rules);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load rules';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function toggleRule(id: string) {
    try {
      await apiClient.post(`/admin/security/threat/rules/${id}/toggle`, {});
      toast.success('Rule toggled');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Toggle failed';
      toast.error(msg);
    }
  }

  async function deleteRule(id: string) {
    try {
      await apiClient.delete(`/admin/security/threat/rules/${id}`);
      toast.success('Rule deleted');
      setDeleting(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="Anomaly Detection Rules"
      icon={Target}
      description="Behavioral rules that watch for credential theft, account takeover, data exfiltration, and abuse patterns."
      actions={
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1.5"
          onClick={() => setCreating(true)}
          title="Create a new anomaly rule"
        >
          <Plus className="h-3 w-3" />
          Add rule
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading rules…
        </div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-muted-foreground p-4 italic">
          No anomaly rules defined yet. Use “Add rule” to create the first one.
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((r) => (
            <AnomalyRuleRow
              key={r.id}
              rule={r}
              onToggle={() => toggleRule(r.id)}
              onEdit={() => setEditing(r)}
              onDelete={() => setDeleting(r)}
            />
          ))}
        </div>
      )}

      {creating && (
        <AnomalyRuleEditorSheet
          mode="create"
          open
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}
      {editing && (
        <AnomalyRuleEditorSheet
          mode="edit"
          rule={editing}
          open
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Delete anomaly rule"
          description={`Delete the rule "${deleting.name}"? Active incidents from this rule will keep their reference but the rule itself is removed.`}
          consequences={[
            `Rule "${deleting.name}" (${RULE_TYPE_LABELS[deleting.ruleType]}) will be permanently deleted`,
            'Existing incidents retain their reference but lose live linkage',
            'Action is logged to the audit chain',
          ]}
          confirmPhrase={deleting.name}
          confirmLabel="Delete rule"
          variant="warning"
          requireReason
          onConfirm={() => deleteRule(deleting.id)}
        />
      )}
    </SectionCard>
  );
}

function AnomalyRuleRow({
  rule, onToggle, onEdit, onDelete,
}: {
  rule: AnomalyRule;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const sevColor =
    rule.severity === 'critical' ? 'border-red-500/40 text-red-700 dark:text-red-300 bg-red-500/5'
    : rule.severity === 'high' ? 'border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/5'
    : rule.severity === 'medium' ? 'border-blue-500/40 text-blue-700 dark:text-blue-300 bg-blue-500/5'
    : 'border-slate-400/40 text-muted-foreground';

  const actionMeta: Record<RuleAction, { label: string; color: string }> = {
    alert_only: { label: 'Alert only', color: 'text-blue-600 dark:text-blue-400' },
    require_mfa: { label: 'Require MFA', color: 'text-amber-600 dark:text-amber-400' },
    block_session: { label: 'Block session', color: 'text-red-600 dark:text-red-400' },
    disable_account: { label: 'Disable account', color: 'text-red-700 dark:text-red-300' },
  };

  return (
    <div
      className={cn(
        'border rounded-md p-3 flex items-center gap-3',
        rule.isEnabled ? 'bg-card' : 'bg-muted/30 opacity-60'
      )}
    >
      <Switch
        checked={rule.isEnabled}
        onCheckedChange={onToggle}
        className="scale-75 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium">{rule.name}</span>
          <Badge variant="outline" className="h-4 text-[9px] px-1.5">
            {RULE_TYPE_LABELS[rule.ruleType]}
          </Badge>
          <Badge variant="outline" className={cn('h-4 text-[9px] px-1.5', sevColor)}>
            {rule.severity}
          </Badge>
          <Badge variant="outline" className={cn('h-4 text-[9px] px-1.5', actionMeta[rule.action].color)}>
            → {actionMeta[rule.action].label}
          </Badge>
          {rule.matchCount24h > 0 && (
            <Badge variant="secondary" className="h-4 text-[9px] px-1.5 gap-1">
              <Activity className="h-2 w-2" />
              {rule.matchCount24h}/24h
            </Badge>
          )}
        </div>
        {rule.description && (
          <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
            {rule.description}
          </p>
        )}
        {rule.lastTriggeredAt && (
          <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="h-2.5 w-2.5" />
            Last triggered {relativeTime(rule.lastTriggeredAt)}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 shrink-0"
        title="Edit rule"
        onClick={onEdit}
      >
        <Pencil className="h-3 w-3" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 shrink-0"
        title="Delete rule"
        onClick={onDelete}
      >
        <Trash2 className="h-3 w-3 text-red-500" />
      </Button>
    </div>
  );
}

function AnomalyRuleEditorSheet({
  mode, rule, open, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  rule?: AnomalyRule;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [ruleType, setRuleType] = useState<RuleType>(rule?.ruleType ?? 'brute_force');
  const [severity, setSeverity] = useState<Severity>(rule?.severity ?? 'medium');
  const [action, setAction] = useState<RuleAction>(rule?.action ?? 'alert_only');
  const [threshold, setThreshold] = useState<Record<string, unknown>>(
    rule?.threshold ?? DEFAULT_THRESHOLDS[ruleType]
  );
  const [saving, setSaving] = useState(false);
  const [confirmingHighRisk, setConfirmingHighRisk] = useState(false);

  // When ruleType changes in create-mode, pull in the default threshold so
  // the user sees the right shape of inputs for the new rule type.
  useEffect(() => {
    if (mode === 'create') {
      setThreshold(DEFAULT_THRESHOLDS[ruleType]);
    }
  }, [ruleType, mode]);

  function updateThreshold(key: string, value: number | string | boolean) {
    setThreshold((prev) => ({ ...prev, [key]: value }));
  }

  async function actuallySave() {
    try {
      setSaving(true);
      if (mode === 'create') {
        await apiClient.post('/admin/security/threat/rules', {
          name: name.trim(),
          description: description.trim() || undefined,
          ruleType,
          severity,
          action,
          threshold,
        });
        toast.success('Rule created');
      } else if (rule) {
        await apiClient.put(`/admin/security/threat/rules/${rule.id}`, {
          name: name.trim(),
          description: description.trim() || null,
          severity,
          action,
          threshold,
        });
        toast.success('Rule updated');
      }
      await onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
      setConfirmingHighRisk(false);
    }
  }

  function handleSave() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    // Action upgrade to disable_account is highest-risk — typed-confirm.
    const wasDisable = rule?.action === 'disable_account';
    if (action === 'disable_account' && !wasDisable) {
      setConfirmingHighRisk(true);
      return;
    }
    void actuallySave();
  }

  const heading = mode === 'create' ? 'New anomaly rule' : (rule?.name ?? 'Edit rule');

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent className="sm:max-w-lg p-0 flex flex-col">
          <SheetHeader className="p-5 border-b shrink-0">
            <SheetTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4" />
              {heading}
            </SheetTitle>
            <SheetDescription className="text-xs">
              {mode === 'create'
                ? 'Define a behavioural rule. Threshold inputs adapt to the chosen rule type.'
                : (rule?.description ?? 'Edit rule severity, action, and tunable thresholds.')}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aggressive brute-force detector"
                className="h-8 text-xs mt-1"
                disabled={saving}
              />
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this rule watches for…"
                className="mt-1 text-[11px] min-h-[60px]"
                disabled={saving}
              />
            </div>

            {mode === 'create' && (
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Rule type</Label>
                <Select
                  value={ruleType}
                  onValueChange={(v) => setRuleType(v as RuleType)}
                  disabled={saving}
                >
                  <SelectTrigger className="h-8 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(RULE_TYPE_LABELS) as RuleType[]).map((t) => (
                      <SelectItem key={t} value={t} className="text-xs">
                        {RULE_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Severity</Label>
              <Select
                value={severity}
                onValueChange={(v) => setSeverity(v as Severity)}
                disabled={saving}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical" className="text-xs">Critical</SelectItem>
                  <SelectItem value="high" className="text-xs">High</SelectItem>
                  <SelectItem value="medium" className="text-xs">Medium</SelectItem>
                  <SelectItem value="low" className="text-xs">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Action when triggered</Label>
              <Select
                value={action}
                onValueChange={(v) => setAction(v as RuleAction)}
                disabled={saving}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alert_only" className="text-xs">Alert only (no enforcement)</SelectItem>
                  <SelectItem value="require_mfa" className="text-xs">Require MFA reauth</SelectItem>
                  <SelectItem value="block_session" className="text-xs">Block session immediately</SelectItem>
                  <SelectItem value="disable_account" className="text-xs">Disable account (highest risk)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <div>
              <Label className="text-xs font-semibold">Thresholds</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">
                Tune detection sensitivity. Lower thresholds catch more, but increase false positives.
              </p>
              {Object.keys(threshold).length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic">
                  This rule has no tunable thresholds.
                </p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(threshold).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide w-44 shrink-0">
                        {k.replace(/([A-Z])/g, ' $1').toLowerCase()}
                      </Label>
                      {typeof v === 'boolean' ? (
                        <Switch
                          checked={v}
                          onCheckedChange={(next) => updateThreshold(k, next)}
                          className="scale-75"
                          disabled={saving}
                        />
                      ) : typeof v === 'number' ? (
                        <Input
                          type="number"
                          value={v}
                          onChange={(e) => updateThreshold(k, parseInt(e.target.value, 10) || 0)}
                          className="h-7 text-[11px] tabular-nums w-24"
                          disabled={saving}
                        />
                      ) : (
                        <Input
                          value={String(v)}
                          onChange={(e) => updateThreshold(k, e.target.value)}
                          className="h-7 text-[11px] flex-1"
                          disabled={saving}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="border-t shrink-0 p-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" className="h-8 text-xs" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
              {mode === 'create' ? 'Create rule' : 'Save changes'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {confirmingHighRisk && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setConfirmingHighRisk(false)}
          title="Upgrade rule action to disable_account"
          description="This rule will start disabling user accounts when it triggers. Disabled accounts require manual review to restore. Confirm by typing the rule name."
          consequences={[
            `Triggers will permanently disable the affected account`,
            'Re-enabling requires a security admin manual review',
            'Action is logged to the audit chain as a high-risk event',
          ]}
          confirmPhrase={name.trim()}
          confirmLabel="Upgrade action"
          variant="warning"
          requireReason
          onConfirm={() => actuallySave()}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Security Incidents (W1b-15)
// ═══════════════════════════════════════════════════════════════════════════

function IncidentsSection() {
  const [incidents, setIncidents] = useState<IncidentSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | IncidentStatus>('all');
  const [filterSeverity, setFilterSeverity] = useState<'all' | Severity>('all');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<IncidentDetail | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('pageSize', '200');
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterSeverity !== 'all') params.set('severity', filterSeverity);

      const data = await apiClient.get<{
        incidents: IncidentSummary[];
        total: number;
      }>(`/admin/security/threat/incidents?${params.toString()}`);
      setIncidents(data.incidents);
      setTotal(data.total);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load incidents';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Client-side text filter on top of server filters.
  const filtered = useMemo(() => {
    if (!search.trim()) return incidents;
    const q = search.toLowerCase();
    return incidents.filter(
      (i) =>
        i.title.toLowerCase().includes(q) ||
        (i.summary?.toLowerCase().includes(q) ?? false) ||
        (i.affectedIp?.includes(search) ?? false)
    );
  }, [incidents, search]);

  async function openDetail(id: string) {
    try {
      const data = await apiClient.get<IncidentDetail>(
        `/admin/security/threat/incidents/${id}`
      );
      setViewing(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load detail';
      toast.error(msg);
    }
  }

  async function transition(
    id: string,
    to: IncidentStatus,
    resolutionNote?: string
  ) {
    try {
      await apiClient.post(`/admin/security/threat/incidents/${id}/transition`, {
        to,
        resolutionNote,
      });
      toast.success(`Incident marked ${to.replace(/_/g, ' ')}`);
      setViewing(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Transition failed';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="Security Incidents"
      icon={ShieldAlert}
      description={`${total} total. Triage open incidents and document resolution for audit.`}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, summary, or IP…"
            className="h-8 text-xs pl-7"
          />
        </div>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as 'all' | IncidentStatus)}>
          <SelectTrigger className="h-7 text-[11px] w-[150px]" title="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All statuses</SelectItem>
            <SelectItem value="open" className="text-xs">Open</SelectItem>
            <SelectItem value="investigating" className="text-xs">Investigating</SelectItem>
            <SelectItem value="resolved" className="text-xs">Resolved</SelectItem>
            <SelectItem value="false_positive" className="text-xs">False positive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSeverity} onValueChange={(v) => setFilterSeverity(v as 'all' | Severity)}>
          <SelectTrigger className="h-7 text-[11px] w-[140px]" title="Filter by severity">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All severities</SelectItem>
            <SelectItem value="critical" className="text-xs">Critical</SelectItem>
            <SelectItem value="high" className="text-xs">High</SelectItem>
            <SelectItem value="medium" className="text-xs">Medium</SelectItem>
            <SelectItem value="low" className="text-xs">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '36%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr className="border-b bg-muted/30 sticky top-0">
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">When</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Severity</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Title</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Affected IP</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[11px] text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />
                      Loading incidents…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[11px] text-muted-foreground">
                      No incidents match the current filters.
                    </td>
                  </tr>
                ) : filtered.map((i) => (
                  <tr
                    key={i.id}
                    className="border-b hover:bg-muted/20 cursor-pointer"
                    onClick={() => openDetail(i.id)}
                  >
                    <td className="px-3 py-2 text-xs tabular-nums">{relativeTime(i.triggeredAt)}</td>
                    <td className="px-3 py-2">
                      <SeverityBadge severity={i.severity} />
                    </td>
                    <td className="px-3 py-2 text-[11px] truncate" title={i.title}>{i.title}</td>
                    <td className="px-3 py-2 text-[10px] text-muted-foreground truncate font-mono">
                      {i.affectedIp ?? '—'}
                    </td>
                    <td className="px-3 py-2">
                      <IncidentStatusBadge status={i.status} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-2 grid grid-cols-3 gap-2 text-[10px]">
        <SummaryStat
          label="Open"
          value={incidents.filter((i) => i.status === 'open').length}
          icon={AlertOctagon}
        />
        <SummaryStat
          label="Investigating"
          value={incidents.filter((i) => i.status === 'investigating').length}
          icon={Eye}
        />
        <SummaryStat
          label="Resolved (loaded)"
          value={incidents.filter((i) => i.status === 'resolved').length}
          icon={CheckCircle2}
        />
      </div>

      {viewing && (
        <IncidentDetailSheet
          incident={viewing}
          open
          onClose={() => setViewing(null)}
          onTransition={(to, note) => transition(viewing.id, to, note)}
        />
      )}
    </SectionCard>
  );
}

function SummaryStat({
  label, value, icon: Icon,
}: { label: string; value: number; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="border rounded-md p-2 flex items-center gap-2">
      <Icon className="h-3 w-3 text-muted-foreground" />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  const cfg =
    severity === 'critical' ? 'border-red-500/40 text-red-700 dark:text-red-300 bg-red-500/5'
    : severity === 'high' ? 'border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/5'
    : severity === 'medium' ? 'border-blue-500/40 text-blue-700 dark:text-blue-300'
    : 'border-slate-400/40 text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('h-4 text-[9px] px-1.5', cfg)}>
      {severity}
    </Badge>
  );
}

function IncidentStatusBadge({ status }: { status: IncidentStatus }) {
  const meta: Record<IncidentStatus, { label: string; color: string; dot: 'healthy' | 'degraded' | 'failed' | 'pending' | 'disabled' }> = {
    open:           { label: 'Open',           color: 'text-red-600 dark:text-red-400',         dot: 'failed' },
    investigating:  { label: 'Investigating',  color: 'text-amber-600 dark:text-amber-400',     dot: 'pending' },
    resolved:       { label: 'Resolved',       color: 'text-emerald-600 dark:text-emerald-400', dot: 'healthy' },
    false_positive: { label: 'False positive', color: 'text-muted-foreground',                  dot: 'disabled' },
  };
  const m = meta[status];
  return (
    <div className="flex items-center gap-1.5">
      <StatusDot status={m.dot} />
      <span className={cn('text-[10px]', m.color)}>{m.label}</span>
    </div>
  );
}

function IncidentDetailSheet({
  incident, open, onClose, onTransition,
}: {
  incident: IncidentDetail;
  open: boolean;
  onClose: () => void;
  onTransition: (to: IncidentStatus, resolutionNote?: string) => void;
}) {
  const [resolution, setResolution] = useState(incident.resolutionNote ?? '');
  const evidenceJson = useMemo(
    () => JSON.stringify(incident.evidence ?? {}, null, 2),
    [incident.evidence]
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="p-5 border-b shrink-0">
          <SheetTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            {incident.title}
          </SheetTitle>
          <SheetDescription className="text-xs flex items-center gap-2">
            <SeverityBadge severity={incident.severity} />
            <IncidentStatusBadge status={incident.status} />
            {incident.ruleType && <span className="font-mono">{incident.ruleType}</span>}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-4">
          {incident.summary && (
            <div className="p-3 border rounded-md bg-muted/30">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Summary</div>
              <p className="text-xs mt-1 leading-relaxed">{incident.summary}</p>
            </div>
          )}

          <Detail label="Triggered" value={new Date(incident.triggeredAt).toLocaleString()} />
          <Detail label="Affected user" value={incident.affectedUserId ?? '—'} mono />
          <Detail label="Affected IP" value={incident.affectedIp ?? '—'} mono />
          <Detail label="Action taken" value={(incident.actionTaken ?? '—').replace(/_/g, ' ')} />
          <Detail label="Assigned to" value={incident.assignedTo ?? 'Unassigned'} mono />

          <Separator />

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Evidence</Label>
            <pre className="mt-1 text-[10px] bg-muted/40 p-2 rounded overflow-x-auto font-mono leading-relaxed">
              {evidenceJson}
            </pre>
          </div>

          {incident.resolvedAt && (
            <>
              <Separator />
              <Detail label="Resolved at" value={new Date(incident.resolvedAt).toLocaleString()} />
              <Detail label="Resolved by" value={incident.resolvedBy ?? '—'} mono />
              {incident.resolutionNote && (
                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Resolution note</Label>
                  <p className="mt-1 text-[11px] p-2.5 bg-muted/40 rounded leading-relaxed">{incident.resolutionNote}</p>
                </div>
              )}
            </>
          )}

          {(incident.status === 'open' || incident.status === 'investigating') && (
            <>
              <Separator />
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Resolution note (required to resolve / mark false-positive)
                </Label>
                <Textarea
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder="Document what you found and what action was taken…"
                  className="mt-1 text-[11px] min-h-[80px]"
                />
              </div>
            </>
          )}
        </div>

        <div className="border-t shrink-0 p-4 flex justify-end gap-2">
          {incident.status === 'open' && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              title="Move to investigating"
              onClick={() => onTransition('investigating')}
            >
              <Eye className="h-3 w-3" />
              Investigate
            </Button>
          )}
          {(incident.status === 'open' || incident.status === 'investigating') && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                title="Mark as false positive"
                disabled={!resolution.trim()}
                onClick={() => onTransition('false_positive', resolution.trim())}
              >
                <XCircle className="h-3 w-3" />
                False positive
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs gap-1.5"
                title="Mark resolved"
                disabled={!resolution.trim()}
                onClick={() => onTransition('resolved', resolution.trim())}
              >
                <CheckCircle2 className="h-3 w-3" />
                Mark resolved
              </Button>
            </>
          )}
          {(incident.status === 'resolved' || incident.status === 'false_positive') && (
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Block List (W1b-16)
// ═══════════════════════════════════════════════════════════════════════════

function BlockListSection() {
  const [entries, setEntries] = useState<BlockListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | BlockListType>('all');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<BlockListEntry | null>(null);
  const [bulkUnblock, setBulkUnblock] = useState<BlockListSource | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('pageSize', '500');
      if (filterType !== 'all') params.set('type', filterType);
      const data = await apiClient.get<{ entries: BlockListEntry[] }>(
        `/admin/security/threat/blocklist?${params.toString()}`
      );
      setEntries(data.entries);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load block list';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function removeEntry(id: string) {
    try {
      await apiClient.delete(`/admin/security/threat/blocklist/${id}`);
      toast.success('Block removed');
      setRemoving(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Remove failed';
      toast.error(msg);
    }
  }

  async function executeBulkUnblock(source: BlockListSource) {
    try {
      const res = await apiClient.post<{ removedCount: number }>(
        '/admin/security/threat/blocklist/bulk-unblock',
        { source, confirmed: true }
      );
      toast.success(`Bulk unblock: removed ${res.removedCount} entries`);
      setBulkUnblock(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bulk unblock failed';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="Block Lists"
      icon={Ban}
      description="IPs, subnets, user agents, API keys, and accounts that should be denied service. Auto-blocks come from anomaly rules; manual entries are admin-flagged."
      actions={
        <div className="flex items-center gap-2">
          <Select value={filterType} onValueChange={(v) => setFilterType(v as 'all' | BlockListType)}>
            <SelectTrigger className="h-7 text-[11px] w-[140px]" title="Filter by type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All types</SelectItem>
              <SelectItem value="ip" className="text-xs">IP</SelectItem>
              <SelectItem value="subnet" className="text-xs">Subnet</SelectItem>
              <SelectItem value="user_agent" className="text-xs">User-Agent</SelectItem>
              <SelectItem value="api_key" className="text-xs">API key</SelectItem>
              <SelectItem value="user_account" className="text-xs">Account</SelectItem>
              <SelectItem value="email_domain" className="text-xs">Email domain</SelectItem>
              <SelectItem value="country" className="text-xs">Country</SelectItem>
              <SelectItem value="asn" className="text-xs">ASN</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5"
            onClick={() => setAdding(true)}
            title="Add a block list entry"
          >
            <Plus className="h-3 w-3" />
            Add block
          </Button>
        </div>
      }
    >
      <Card>
        <CardContent className="p-0">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '11%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Type</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Value</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Reason</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Source</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Expires</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Hits</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />
                    Loading block list…
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[11px] text-muted-foreground">
                    No blocked entries.
                  </td>
                </tr>
              ) : entries.map((b) => (
                <tr key={b.id} className="border-b hover:bg-muted/20">
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="h-4 text-[9px] px-1.5">
                      {b.listType.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono truncate" title={b.value}>{b.value}</td>
                  <td className="px-3 py-2 text-[10px] text-muted-foreground truncate" title={b.reason ?? ''}>
                    {b.reason ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-muted-foreground">
                    {b.source.replace(/_/g, ' ')}
                  </td>
                  <td className="px-3 py-2 text-[10px] tabular-nums">
                    {b.expiresAt ? new Date(b.expiresAt).toLocaleDateString() : 'Permanent'}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-right">{b.hitCount.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      title="Remove block"
                      onClick={() => setRemoving(b)}
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-muted-foreground">Bulk unblock by source:</span>
        <Button
          variant="outline"
          size="sm"
          className="h-6 text-[10px] gap-1.5"
          onClick={() => setBulkUnblock('manual')}
          title="Remove every manually-added block"
        >
          <Trash2 className="h-2.5 w-2.5" />
          Manual entries
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-6 text-[10px] gap-1.5"
          onClick={() => setBulkUnblock('anomaly_rule')}
          title="Remove every block created by an anomaly rule"
        >
          <Trash2 className="h-2.5 w-2.5" />
          Auto-blocks (anomaly rule)
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-6 text-[10px] gap-1.5"
          onClick={() => setBulkUnblock('threat_intel_feed')}
          title="Remove every block imported from threat-intel feeds"
        >
          <Trash2 className="h-2.5 w-2.5" />
          Threat-intel feed
        </Button>
      </div>

      {adding && (
        <AddBlockDialog
          open
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      )}
      {removing && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setRemoving(null)}
          title="Remove block"
          description={`Remove block on "${removing.value}"? Traffic from this entity will be allowed again. The block had ${removing.hitCount.toLocaleString()} hits while active.`}
          consequences={[
            `${removing.listType.replace(/_/g, ' ')} ${removing.value} will no longer be blocked`,
            'If the threat source returns, anomaly rules may re-block automatically',
            'Action is logged to the audit chain',
          ]}
          confirmPhrase={removing.value}
          confirmLabel="Remove block"
          variant="warning"
          requireReason
          onConfirm={() => removeEntry(removing.id)}
        />
      )}
      {bulkUnblock && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setBulkUnblock(null)}
          title={`Bulk unblock — ${bulkUnblock.replace(/_/g, ' ')}`}
          description={`This removes EVERY block list entry whose source is "${bulkUnblock}". This is a 2-person action in v1.1; v1 gates on typed confirmation.`}
          consequences={[
            `All entries with source="${bulkUnblock}" will be deleted`,
            'Anomaly rules will re-create their auto-blocks on next match',
            'Action is logged to the audit chain as a high-risk event',
          ]}
          confirmPhrase={`bulk unblock`}
          confirmLabel="Bulk unblock"
          variant="warning"
          requireReason
          onConfirm={() => executeBulkUnblock(bulkUnblock)}
        />
      )}
    </SectionCard>
  );
}

function AddBlockDialog({
  open, onClose, onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => Promise<void> | void;
}) {
  const [listType, setListType] = useState<BlockListType>('ip');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [permanent, setPermanent] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!value.trim()) {
      toast.error('Value is required');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post('/admin/security/threat/blocklist', {
        listType,
        value: value.trim(),
        source: 'manual',
        reason: reason.trim() || undefined,
        expiresAt: permanent
          ? null
          : new Date(Date.now() + expiresInDays * 86_400_000).toISOString(),
      });
      toast.success(`${value} blocked`);
      await onAdded();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Add failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Ban className="h-4 w-4" />
            Add block
          </DialogTitle>
          <DialogDescription className="text-xs">
            Manually block traffic from a specific source. Use sparingly — anomaly rules handle most cases automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Block type</Label>
            <Select value={listType} onValueChange={(v) => setListType(v as BlockListType)} disabled={submitting}>
              <SelectTrigger className="h-8 text-xs mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ip" className="text-xs">IP address</SelectItem>
                <SelectItem value="subnet" className="text-xs">Subnet (CIDR)</SelectItem>
                <SelectItem value="user_agent" className="text-xs">User-Agent (regex)</SelectItem>
                <SelectItem value="api_key" className="text-xs">API key</SelectItem>
                <SelectItem value="user_account" className="text-xs">User account</SelectItem>
                <SelectItem value="email_domain" className="text-xs">Email domain</SelectItem>
                <SelectItem value="country" className="text-xs">Country (ISO-2)</SelectItem>
                <SelectItem value="asn" className="text-xs">ASN</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Value</Label>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                listType === 'ip' ? '203.0.113.42'
                : listType === 'subnet' ? '203.0.113.0/24'
                : listType === 'user_agent' ? 'curl/.*'
                : listType === 'api_key' ? 'key-id'
                : listType === 'email_domain' ? 'spam.example.com'
                : listType === 'country' ? 'CN'
                : listType === 'asn' ? 'AS12345'
                : 'user-id'
              }
              className="h-8 text-xs mt-1 font-mono"
              disabled={submitting}
            />
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you blocking this?"
              className="mt-1 text-[11px] min-h-[60px]"
              disabled={submitting}
            />
          </div>

          <div className="flex items-center gap-2 p-2 border rounded bg-muted/20">
            <Switch
              checked={permanent}
              onCheckedChange={setPermanent}
              className="scale-75"
              disabled={submitting}
            />
            <Label className="text-xs flex-1">Permanent block</Label>
          </div>

          {!permanent && (
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Expires in</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10) || 0)}
                  className="h-8 w-24 text-xs tabular-nums"
                  disabled={submitting}
                />
                <span className="text-[10px] text-muted-foreground">days</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" className="h-8 text-xs gap-1.5" onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
            <Ban className="h-3 w-3" />
            Add block
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3 text-xs">
      <div className="w-32 shrink-0 text-[10px] text-muted-foreground uppercase tracking-wide pt-0.5">
        {label}
      </div>
      <div className={cn('flex-1 min-w-0 break-words', mono && 'font-mono text-[11px]')}>
        {value}
      </div>
    </div>
  );
}
